import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ShieldCheck, Star, MapPin, Filter } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

const SPECIALTIES = ["Skin Fades", "Beard", "Classic Cuts", "Textured Crops", "Color", "Design", "Braids"];

export default function Directory() {
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [city, setCity] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [certifiedOnly, setCertifiedOnly] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (city) params.city = city;
      if (specialty) params.specialty = specialty;
      if (certifiedOnly) params.certified = true;
      const { data } = await api.get("/barbers", { params });
      setBarbers(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16">
      <div className="max-w-2xl">
        <p className="font-mono text-xs uppercase tracking-widest text-[#D4AF37] mb-3">Discover</p>
        <h1 className="font-display text-4xl md:text-5xl font-bold">Find serious barbers.</h1>
        <p className="mt-4 text-neutral-400">Certified pros, verified craft, and real trust markers. Filter by city, specialty, and certification.</p>
      </div>

      <div className="mt-10 grid lg:grid-cols-[280px_1fr] gap-8">
        {/* filters */}
        <aside className="lg:sticky lg:top-24 h-fit space-y-6 bg-[#0a0a0a] border border-white/10 p-6">
          <div className="flex items-center gap-2 text-[#D4AF37]"><Filter size={16} /><span className="font-mono text-xs uppercase tracking-wider">Filters</span></div>

          <div>
            <label className="text-xs text-neutral-400 mb-2 block">Search</label>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or shop" data-testid="directory-search"
                className="pl-9 bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" onKeyDown={(e) => e.key === "Enter" && load()} />
            </div>
          </div>

          <div>
            <label className="text-xs text-neutral-400 mb-2 block">City</label>
            <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="e.g. Los Angeles" data-testid="directory-city"
              className="bg-[#111] border-white/10 rounded-none focus-visible:ring-[#D4AF37]" onKeyDown={(e) => e.key === "Enter" && load()} />
          </div>

          <div>
            <label className="text-xs text-neutral-400 mb-2 block">Specialty</label>
            <div className="flex flex-wrap gap-2">
              {SPECIALTIES.map((s) => (
                <button key={s} onClick={() => setSpecialty(specialty === s ? "" : s)} data-testid={`directory-spec-${s.toLowerCase().replace(/\s/g, "-")}`}
                  className={`text-xs px-2.5 py-1.5 border transition-colors ${specialty === s ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-neutral-400 hover:border-white/30"}`}>
                  {s}
                </button>
              ))}
            </div>
          </div>

          <label className="flex items-center gap-2.5 cursor-pointer">
            <Checkbox checked={certifiedOnly} onCheckedChange={setCertifiedOnly} data-testid="directory-certified-only" className="border-white/30 data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]" />
            <span className="text-sm text-neutral-300">Certified only</span>
          </label>

          <Button onClick={load} className="w-full rounded-none bg-[#D4AF37] text-black hover:bg-[#F0C84B] font-semibold" data-testid="directory-apply">Apply Filters</Button>
        </aside>

        {/* grid */}
        <div>
          {loading ? (
            <div className="py-24 flex justify-center"><div className="h-8 w-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" /></div>
          ) : barbers.length === 0 ? (
            <div className="py-24 text-center text-neutral-500" data-testid="directory-empty">No barbers match your filters.</div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3">
              {barbers.map((b) => (
                <Link key={b.id} to={`/barber/${b.id}`} data-testid={`barber-card-${b.id}`}
                  className={`group block bg-[#111] border overflow-hidden transition-transform duration-300 hover:-translate-y-1 ${b.featured ? "border-[#D4AF37]/50 gold-glow" : "border-white/10 hover:border-[#D4AF37]/40"}`}>
                  <div className="relative h-52 overflow-hidden">
                    <img src={b.avatar} alt={b.display_name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                    {b.certified && (
                      <span className="absolute top-3 left-3 flex items-center gap-1 bg-[#D4AF37] text-black text-xs font-mono px-2 py-1"><ShieldCheck size={12} /> Certified</span>
                    )}
                    {b.featured && (
                      <span className="absolute top-3 right-3 flex items-center gap-1 bg-black/70 border border-[#D4AF37]/50 text-[#D4AF37] text-xs font-mono px-2 py-1"><Star size={12} /> Featured</span>
                    )}
                  </div>
                  <div className="p-5">
                    <h3 className="font-display text-lg font-semibold">{b.display_name}</h3>
                    <p className="text-sm text-neutral-400">{b.shop_name}</p>
                    <div className="mt-3 flex items-center justify-between text-xs text-neutral-500">
                      <span className="flex items-center gap-1"><MapPin size={12} /> {b.city}{b.state ? `, ${b.state}` : ""}</span>
                      <span className="flex items-center gap-1 text-[#D4AF37]"><Star size={12} fill="#D4AF37" /> {b.rating?.toFixed(1)}</span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {(b.specialties || []).slice(0, 3).map((s) => (
                        <span key={s} className="text-[11px] px-2 py-0.5 border border-white/10 text-neutral-400">{s}</span>
                      ))}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
